import { create } from 'zustand';
import type {
  ChatRef,
  ChatMentionsMenuItemType,
} from '@terra/ui-design-system';
import { openCopilotPanel } from '../hooks/useMentions';
import { useChatTabsStore } from './chatTabsStore';

type AddMentionFn = (mention: ChatMentionsMenuItemType) => void;

interface ChatStore {
  chatRef: React.RefObject<ChatRef | null> | null;
  addMentionFn: AddMentionFn | null;
  setChatRef: (ref: React.RefObject<ChatRef | null>) => void;
  setAddMentionFn: (fn: AddMentionFn | null) => void;
  setInput: (text: string) => void;
  appendToInput: (text: string) => void;
  appendTextToInput: (text: string) => void;
  /** Send one or more mentions to chat with rich display */
  sendHttpMentionsToChat: ({
    mentions,
    contextMessage,
    linkedObject,
  }: {
    mentions: ChatMentionsMenuItemType[];
    contextMessage?: string;
    linkedObject?: {
      kind: string;
      id: string;
      label?: string;
    };
  }) => void;
}

type SendHttpMentionsToChatParams = {
  mentions: ChatMentionsMenuItemType[];
  contextMessage?: string;
  linkedObject?: {
    kind: string;
    id: string;
    label?: string;
  };
};

const appendTextToChat = ({
  chatRef,
  text,
}: {
  chatRef: React.RefObject<ChatRef | null> | null;
  text?: string;
}) => {
  if (!chatRef?.current || !text) {
    return;
  }

  chatRef.current.appendText(text);
};

const focusChat = ({
  chatRef,
}: {
  chatRef: React.RefObject<ChatRef | null> | null;
}) => {
  if (chatRef?.current) {
    chatRef.current.focus();
  }
};

const addMentions = ({
  addMentionFn,
  mentions,
}: {
  addMentionFn: AddMentionFn | null;
  mentions: ChatMentionsMenuItemType[];
}) => {
  if (!addMentionFn) {
    return;
  }

  for (const mention of mentions) {
    addMentionFn(mention);
  }
};

export const useChatStore = create<ChatStore>((set, get) => ({
  chatRef: null,
  addMentionFn: null,

  setChatRef: (ref) => set({ chatRef: ref }),

  setAddMentionFn: (fn) => set({ addMentionFn: fn }),

  setInput: (text: string) => {
    openCopilotPanel();
    const { chatRef } = get();
    if (chatRef?.current) {
      chatRef.current.setInput(text);
      chatRef.current.focus();
    }
  },

  appendToInput: (text: string) => {
    openCopilotPanel();
    const { chatRef } = get();
    if (chatRef?.current) {
      chatRef.current.appendText(text);
      chatRef.current.focus();
    }
  },

  appendTextToInput: (text: string) => {
    openCopilotPanel();
    const { chatRef } = get();
    if (chatRef?.current) {
      chatRef.current.appendText(text);
      chatRef.current.focus();
    }
  },

  sendHttpMentionsToChat: ({
    mentions,
    contextMessage,
    linkedObject,
  }: SendHttpMentionsToChatParams) => {
    const { chatRef, addMentionFn } = get();

    addMentions({ addMentionFn, mentions });
    appendTextToChat({ chatRef, text: contextMessage });
    focusChat({ chatRef });

    if (linkedObject) {
      const { activeChatTabId, updateChatTab } = useChatTabsStore.getState();
      if (activeChatTabId) {
        updateChatTab(activeChatTabId, { linkedObject });
      }
    }
  },
}));
